import React from 'react';

interface MarkdownProps {
    text: string;
    className?: string;
    style?: React.CSSProperties;
}

/** Lightweight Slack-style markdown renderer. No dependencies. XSS-safe. */
export function Markdown({ text, className, style }: MarkdownProps) {
    if (!text) return null;

    // Parse markdown into React nodes (line by line)
    const lines = text.split('\n');
    const elements: React.ReactNode[] = [];

    let inCodeBlock = false;
    let codeLines: string[] = [];

    const parseInline = (line: string, key: string | number): React.ReactNode => {
        // Strip trailing backslash which is sometimes used for explicit line breaks in other Markdown flavors
        line = line.replace(/\\$/, '');

        // Split on markdown tokens while preserving delimiters
        const parts: React.ReactNode[] = [];
        let remaining = line;
        let idx = 0;

        // Process inline patterns iteratively. Evaluation order matters.
        const patterns: [RegExp, (m: RegExpMatchArray) => React.ReactNode][] = [
            // Underline: <u>text</u>
            [/<u>([^<]+)<\/u>/, m => <u key={idx++}>{m[1]}</u>],
            // Link: [text](url)
            [/\[([^\]]+)\]\(([^)]+)\)/, m => <a key={idx++} href={m[2]} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--accent-primary)', textDecoration: 'underline' }}>{m[1]}</a>],
            // Bare URLs
            [/(https?:\/\/[^\s<>'"]+)/, m => <a key={idx++} href={m[1]} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--accent-primary)', textDecoration: 'underline' }}>{m[1]}</a>],
            // Inline code: `code`
            [/`([^`]+)`/, m => <code key={idx++} style={{ background: 'var(--bg-tertiary)', padding: '2px 5px', borderRadius: 4, fontFamily: 'monospace', fontSize: '0.9em', border: '1px solid var(--border-color)', boxDecorationBreak: 'clone', WebkitBoxDecorationBreak: 'clone' }}>{m[1]}</code>],
            // Bold: **text** or __text__
            [/\*\*([^\*]+)\*\*/, m => <strong key={idx++}>{m[1]}</strong>],
            [/__([^_]+)__/, m => <strong key={idx++}>{m[1]}</strong>],
            // Italic: *text* or _text_
            [/\*([^\*]+)\*/, m => <em key={idx++}>{m[1]}</em>],
            [/_([^_]+)_/, m => <em key={idx++}>{m[1]}</em>],
            // Strikethrough: ~text~
            [/~([^~]+)~/, m => <del key={idx++}>{m[1]}</del>],
        ];

        while (remaining.length > 0) {
            let earliest: { index: number; match: RegExpMatchArray; renderer: (m: RegExpMatchArray) => React.ReactNode } | null = null;

            for (const [pattern, renderer] of patterns) {
                const m = remaining.match(pattern);
                if (m && m.index !== undefined) {
                    if (!earliest || m.index < earliest.index) {
                        earliest = { index: m.index, match: m, renderer };
                    }
                }
            }

            if (!earliest) {
                parts.push(remaining);
                break;
            }

            // Text before the match
            if (earliest.index > 0) {
                parts.push(remaining.slice(0, earliest.index));
            }

            // Render the match
            parts.push(earliest.renderer(earliest.match));

            remaining = remaining.slice(earliest.index + earliest.match[0].length);
        }

        return <React.Fragment key={key}>{parts}</React.Fragment>;
    };

    for (let i = 0; i < lines.length; i++) {
        const line = lines[i];

        // Fenced code block
        if (line.startsWith('```')) {
            if (inCodeBlock) {
                elements.push(
                    <pre key={i} style={{ background: 'var(--bg-tertiary)', padding: '8px 12px', borderRadius: 6, overflowX: 'auto', fontFamily: 'monospace', fontSize: 13 }}>
                        <code>{codeLines.join('\n')}</code>
                    </pre>
                );
                codeLines = [];
                inCodeBlock = false;
            } else {
                inCodeBlock = true;
            }
            continue;
        }

        if (inCodeBlock) {
            codeLines.push(line);
            continue;
        }

        // Blockquote
        if (line.startsWith('> ')) {
            elements.push(
                <blockquote key={i} style={{ borderLeft: '3px solid var(--accent-primary)', padding: '4px 12px', margin: '8px 0', color: 'var(--text-secondary)', fontStyle: 'italic', background: 'var(--bg-tertiary)', borderRadius: '0 4px 4px 0' }}>
                    {parseInline(line.slice(2), i)}
                </blockquote>
            );
            continue;
        }

        // Unordered list item
        if (/^- /.test(line)) {
            elements.push(
                <div key={i} style={{ display: 'flex', gap: 6, alignItems: 'flex-start' }}>
                    <span style={{ marginTop: 4, width: 5, height: 5, borderRadius: '50%', background: 'currentColor', flexShrink: 0 }} />
                    <span>{parseInline(line.slice(2), i)}</span>
                </div>
            );
            continue;
        }

        // Ordered list item
        const olMatch = line.match(/^(\d+)\. (.+)/);
        if (olMatch) {
            elements.push(
                <div key={i} style={{ display: 'flex', gap: 6, alignItems: 'flex-start' }}>
                    <span style={{ flexShrink: 0, minWidth: 18, fontSize: 13 }}>{olMatch[1]}.</span>
                    <span>{parseInline(olMatch[2], i)}</span>
                </div>
            );
            continue;
        }

        // Empty line → small spacer
        if (line.trim() === '') {
            elements.push(<br key={i} />);
            continue;
        }

        // Normal paragraph line
        elements.push(
            <span key={i} style={{ display: 'block' }}>
                {parseInline(line, i)}
            </span>
        );
    }

    return (
        <div className={className} style={{ wordBreak: 'break-word', whiteSpace: 'pre-wrap', lineHeight: '1.6', ...style }}>
            {elements}
        </div>
    );
}
